// Port of Flutter's linker.dart sendData() — the whole login decision tree, with
// no UI in it, so the mobile and desktop shells can both drive it.
//
// The backend enforces ONE device per account via Users.DeviceName. The flow:
//
//   credentials wrong                    -> { status: 'invalid' }
//   master/test account (1234567890/1234)-> { status: 'ok' }            (no device check)
//   DeviceName empty (never bound)       -> bind this install, { status: 'ok' }
//   DeviceName != this install           -> { status: 'otp', otp, user } — caller
//                                           collects the WhatsApp OTP, then calls
//                                           confirmDeviceBinding()
//   DeviceName == this install           -> { status: 'ok' }
//
// 'ok' carries the user array the caller should store as the session.

import { login, sendOtp, updateUserDeviceName } from '../api/api';
import { getDeviceId, acceptableDeviceIds } from '../utils/deviceId';
import { isMasterAccount } from './userGate';

export async function attemptLogin(mobileNumber, password) {
  let rows;
  try {
    const res = await login(mobileNumber, password);
    rows = Array.isArray(res.data) ? res.data : [];
  } catch (e) {
    return { status: 'error', error: e };
  }

  if (!rows.length) return { status: 'invalid' };

  const user = rows[0];
  const deviceId = await getDeviceId();

  if (isMasterAccount(user)) return { status: 'ok', userData: rows };

  const registered = String(user.DeviceName ?? '');

  // Never bound to a phone before — claim it silently.
  if (registered === '') {
    await updateUserDeviceName(user.UserId, deviceId).catch(() => null);
    return { status: 'ok', userData: rows.map((r, i) => (i === 0 ? { ...r, DeviceName: deviceId } : r)) };
  }

  // Already bound to THIS phone — possibly under the form a sibling app writes
  // (smartify-automation stores the model, SmartLifeIndia-flutter Build.ID), so
  // any accepted form counts. Without this, the two apps would bounce the
  // account between values and each would lock the other out on one phone.
  const knownIds = await acceptableDeviceIds();
  if (knownIds.includes(registered)) return { status: 'ok', userData: rows };

  // Bound to a DIFFERENT phone: prove ownership over WhatsApp before moving it.
  if (registered !== deviceId) {
    const otp = await requestOtp(user.MobileNumber);
    return { status: 'otp', otp, userData: rows, mobileNumber, password };
  }

  return { status: 'ok', userData: rows };
}

// Sends the WhatsApp OTP and returns it. The backend hands the code straight
// back in the response (rows[0].Otp) — the Flutter client compares locally, and
// so do we, to keep the wire contract identical.
export async function requestOtp(mobileNumber) {
  try {
    const res = await sendOtp(String(mobileNumber));
    const rows = Array.isArray(res.data) ? res.data : [];
    return rows[0] && rows[0].Otp != null ? String(rows[0].Otp) : null;
  } catch (e) {
    return null;
  }
}

// Called after the user typed the right OTP: move the account to this install
// and re-read the user row so the stored session matches the server.
export async function confirmDeviceBinding(userData, mobileNumber, password) {
  const user = userData[0];
  const deviceId = await getDeviceId();
  await updateUserDeviceName(user.UserId, deviceId).catch(() => null);
  try {
    const res = await login(mobileNumber, password);
    const rows = Array.isArray(res.data) ? res.data : [];
    if (rows.length) return rows;
  } catch (e) { /* fall through to the local copy */ }
  return userData.map((r, i) => (i === 0 ? { ...r, DeviceName: deviceId } : r));
}
